import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Search, DollarSign, BookOpen, GraduationCap, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const AdmissionFeeSetting = () => {
  const [settings, setSettings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSetting, setCurrentSetting] = useState(null);
  const [formData, setFormData] = useState({
    course_id: '',
    regular_admission_fee: '',
    lateral_admission_fee: '',
    session_id: '',
    description: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, coursesRes, sessionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admission-fee-settings/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/master-settings/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/master-settings/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSettings(settingsRes.data);
      setCourses(coursesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const course = courses.find(c => c.id === formData.course_id);
      const session = sessions.find(s => s.id === formData.session_id);

      const submitData = {
        ...formData,
        course_name: course?.name || '',
        session_name: session?.name || '',
        regular_admission_fee: parseFloat(formData.regular_admission_fee),
        lateral_admission_fee: course?.lateral_entry_allowed 
          ? parseFloat(formData.lateral_admission_fee) 
          : parseFloat(formData.regular_admission_fee)
      };

      let response;
      if (editMode) {
        response = await axios.put(
          `${API_URL}/api/admission-fee-settings/${currentSetting.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Check for fee locking warning from backend
        if (response.data?._warning) {
          toast.warning(response.data._warning, {
            duration: 8000,
            icon: <AlertTriangle className="h-5 w-5 text-amber-500" />
          });
        }
        toast.success('Admission fee setting updated successfully!');
      } else {
        response = await axios.post(
          `${API_URL}/api/admission-fee-settings/`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Admission fee setting created successfully!');
      }

      setIsDialogOpen(false);
      setEditMode(false);
      setCurrentSetting(null);
      setFormData({
        course_id: '',
        regular_admission_fee: '',
        lateral_admission_fee: '',
        session_id: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error saving admission fee setting:', error);
      toast.error(error.response?.data?.detail || 'Failed to save admission fee setting');
    }
  };

  const handleEdit = (setting) => {
    setCurrentSetting(setting);
    setFormData({
      course_id: setting.course_id,
      regular_admission_fee: setting.regular_admission_fee.toString(),
      lateral_admission_fee: setting.lateral_admission_fee.toString(),
      session_id: setting.session_id || '',
      description: setting.description || ''
    });
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this admission fee setting?')) {
      try {
        await axios.delete(`${API_URL}/api/admission-fee-settings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Admission fee setting deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Error deleting admission fee setting:', error);
        alert('Failed to delete admission fee setting');
      }
    }
  };

  const filteredSettings = settings.filter(setting =>
    setting.course_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    setting.session_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admission-fee-setting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Admission Fee Settings</h1>
          <p className="text-gray-600 mt-1">Set different admission fees for Regular and Lateral Entry students</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditMode(false);
            setCurrentSetting(null);
            setFormData({
              course_id: '',
              regular_admission_fee: '',
              lateral_admission_fee: '',
              session_id: '',
              description: ''
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus size={20} className="mr-2" /> Add Fee Setting
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Admission Fee Setting' : 'Add New Admission Fee Setting'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="course_id">Course *</Label>
                  <select
                    id="course_id"
                    className="w-full h-10 border border-gray-300 rounded-md px-3"
                    value={formData.course_id}
                    onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name} ({course.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="session_id">Session</Label>
                  <select
                    id="session_id"
                    className="w-full h-10 border border-gray-300 rounded-md px-3"
                    value={formData.session_id}
                    onChange={(e) => setFormData({ ...formData, session_id: e.target.value })}
                  >
                    <option value="">All Sessions</option>
                    {filterSessionsByCourse(sessions, formData.course_id).map(session => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Label htmlFor="regular_admission_fee" className="text-blue-900 font-semibold">
                    Regular Entry Admission Fee (₹) *
                  </Label>
                  <Input
                    id="regular_admission_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.regular_admission_fee}
                    onChange={(e) => setFormData({ ...formData, regular_admission_fee: e.target.value })}
                    placeholder="10000"
                    required
                    className="mt-2"
                  />
                  <p className="text-xs text-blue-600 mt-2">For students joining from 1st semester</p>
                </div>

                {(() => {
                  const selectedCourse = courses.find(c => c.id === formData.course_id);
                  if (selectedCourse?.lateral_entry_allowed) {
                    return (
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <Label htmlFor="lateral_admission_fee" className="text-orange-900 font-semibold">
                          Lateral Entry Admission Fee (₹) *
                        </Label>
                        <Input
                          id="lateral_admission_fee"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.lateral_admission_fee}
                          onChange={(e) => setFormData({ ...formData, lateral_admission_fee: e.target.value })}
                          placeholder="7000"
                          required
                          className="mt-2"
                        />
                        <p className="text-xs text-orange-600 mt-2">
                          For students joining from {selectedCourse?.lateral_entry_semester || 3}rd semester
                        </p>
                      </div>
                    );
                  } else if (formData.course_id) {
                    return (
                      <div className="flex items-center justify-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-500">Lateral Entry not available for this course</p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex items-center justify-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-400">Please select a course first</p>
                      </div>
                    );
                  }
                })()}
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <textarea
                  id="description"
                  className="w-full border border-gray-300 rounded-lg p-2 min-h-[80px]"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional notes about this fee setting..."
                />
              </div>

              <Button type="submit" className="w-full">
                {editMode ? 'Update Fee Setting' : 'Create Fee Setting'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <Input
            placeholder="Search by course or session..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Fee Settings Grid */}
      {filteredSettings.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
          <DollarSign size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">No admission fee settings found.</p>
          <p className="text-sm text-gray-400">Create your first admission fee setting for Regular and Lateral Entry students.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSettings.map((setting) => {
            const course = courses.find(c => c.id === setting.course_id);
            return (
            <div
              key={setting.id}
              className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-3 rounded-lg">
                    <GraduationCap size={28} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg">{setting.course_name}</h3>
                    {setting.session_name && (
                      <p className="text-xs text-gray-500">{setting.session_name}</p>
                    )}
                    {course?.lateral_entry_allowed && (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Lateral Entry Allowed
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Regular Entry Fee */}
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen size={16} className="text-blue-600" />
                    <p className="text-xs font-medium text-blue-800">Regular Entry (1st Sem)</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">
                    ₹{setting.regular_admission_fee?.toLocaleString('en-IN') || 0}
                  </p>
                </div>

                {/* Lateral Entry Fee - Only show if course allows */}
                {course?.lateral_entry_allowed ? (
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen size={16} className="text-orange-600" />
                      <p className="text-xs font-medium text-orange-800">Lateral Entry ({course?.lateral_entry_semester || 3}rd Sem)</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">
                      ₹{setting.lateral_admission_fee?.toLocaleString('en-IN') || 0}
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-sm text-gray-400 text-center">No Lateral Entry</p>
                  </div>
                )}
              </div>

              {setting.description && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">{setting.description}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                  onClick={() => handleEdit(setting)}
                >
                  <Edit size={16} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(setting.id)}
                >
                  <Trash2 size={16} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          )})}
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
          <p className="text-blue-700 text-sm font-medium">Total Settings</p>
          <p className="text-3xl font-bold text-blue-600 mt-2">{settings.length}</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <p className="text-green-700 text-sm font-medium">Avg Regular Fee</p>
          <p className="text-3xl font-bold text-green-600 mt-2">
            ₹{settings.length > 0
              ? Math.round(settings.reduce((sum, s) => sum + s.regular_admission_fee, 0) / settings.length).toLocaleString('en-IN')
              : 0}
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-xl border border-orange-200">
          <p className="text-orange-700 text-sm font-medium">Avg Lateral Fee</p>
          <p className="text-3xl font-bold text-orange-600 mt-2">
            ₹{settings.length > 0
              ? Math.round(settings.reduce((sum, s) => sum + s.lateral_admission_fee, 0) / settings.length).toLocaleString('en-IN')
              : 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdmissionFeeSetting;
